import React, { useState } from 'react';
import {
  Typography,
  Stack,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
} from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { useQuery } from 'react-query';
import { capitalize } from 'lodash';

import { MainLayout } from '../components/layout/MainLayout';
import { api, callsAPI, campaignAPI } from '../services/api';
import { Call } from '../services/interfaces';
import { useToast } from '../components/common/ToastProvider';

const formatDate = (dateString?: string) => {
  if (!dateString) return '-';
  const d = new Date(dateString);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const formatPhone = (phone?: string) => {
  if (!phone) return '-';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `+1 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
      6
    )}`;
  }
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(
      7
    )}`;
  }
  return phone;
};

const isDateString = (val: unknown): val is string => {
  if (typeof val !== 'string') return false;
  return /^\d{4}-\d{2}-\d{2}T/.test(val);
};

const camelToTitle = (str: string) => {
  return str
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
};

const CAMPAIGN_FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  status: 'Status',
  description: 'Description',
  createdAt: 'Created At',
  startedAt: 'Started At',
  completedAt: 'Completed At',
  pausedAt: 'Paused At',
  resumedAt: 'Resumed At',
  errorMessage: 'Error Message',
  pauseReason: 'Pause Reason',
  timezone: 'Timezone',
  maxCallDurationSec: 'Call Duration',
  ttsVoiceModel: 'Voice',
  businessDays: 'Business Days',
  businessHours: 'Start Time - End Time',
};

const CAMPAIGN_FIELD_ORDER = [
  'name',
  'status',
  'description',
  'createdAt',
  'startedAt',
  'completedAt',
  'pausedAt',
  'resumedAt',
  'errorMessage',
  'pauseReason',
  'timezone',
  'maxCallDurationSec',
  'ttsVoiceModel',
  'businessDays',
  'businessHours',
];

const FIELD_LABELS_CALL: Record<string, string> = {
  id: 'ID',
  answeredAt: 'Answered At',
  attemptNumber: 'Attempt Number',
  createdAt: 'Created At',
  durationSec: 'Duration (sec)',
  endedAt: 'Ended At',
  endedBy: 'Ended By',
  errorMessage: 'Error Message',
  isActive: 'Is Active',
  maxAttempts: 'Max Attempts',
  maxDurationSec: 'Max Duration (sec)',
  recordingPath: 'Recording Path',
  recordingUrl: 'Recording URL',
  startedAt: 'Started At',
  status: 'Status',
  result: 'Result',
  transcript: 'Transcript',
  twilioCallSid: 'Twilio Call SID',
  updatedAt: 'Updated At',
};

const FIELD_ORDER_CALL = [
  'status',
  'result',
  'startedAt',
  'answeredAt',
  'endedAt',
  'endedBy',
  'durationSec',
  'maxDurationSec',
  'maxAttempts',
  'attemptNumber',
  'errorMessage',
  'createdAt',
  'transcript',
];

export const OutgoingCallsPage: React.FC = () => {
  const { showToast } = useToast();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [selectedCampaignName, setSelectedCampaignName] = useState<string>('');

  const getCallsQuery = useQuery(
    ['callsAPI.getCalls', page, selectedCampaignName],
    () =>
      callsAPI.getCalls({
        page,
        campaignName: selectedCampaignName || undefined,
      }),
    {
      keepPreviousData: true,
      onError: (err) => {
        console.error(err);
        showToast('Failed to load calls', 'error');
      },
    }
  );

  const getCampaignsForFilterQuery = useQuery(
    ['campaignAPI.getCampaignsForFilter'],
    () => campaignAPI.getCampaignsForFilter(),
    {
      onError: (err) => {
        console.error(err);
        showToast('Failed to load campaigns', 'error');
      },
    }
  );

  const [viewCall, setViewCall] = useState<Call | null>(null);
  const [viewCallOpen, setViewCallOpen] = useState(false);

  const [audioErrorsShown, setAudioErrorsShown] = useState<Set<string>>(
    new Set()
  );

  const [viewCampaign, setViewCampaign] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [viewCampaignOpen, setViewCampaignOpen] = useState(false);

  const [viewContact, setViewContact] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [viewContactOpen, setViewContactOpen] = useState(false);

  const handleOpenViewCall = (call: Call) => {
    setViewCall(call);
    setViewCallOpen(true);
  };

  const handleCloseViewCall = () => {
    setViewCallOpen(false);
    setViewCall(null);
  };

  const handleOpenViewCampaign = (campaign: Record<string, unknown>) => {
    setViewCampaign(campaign);
    setViewCampaignOpen(true);
  };

  const handleCloseViewCampaign = () => {
    setViewCampaignOpen(false);
    setViewCampaign(null);
  };

  const handleOpenViewContact = (contact: Record<string, unknown>) => {
    setViewContact(contact);
    setViewContactOpen(true);
  };

  const handleCloseViewContact = () => {
    setViewContactOpen(false);
    setViewContact(null);
  };

  const handleAudioError = async (callId: string) => {
    if (audioErrorsShown.has(callId)) return;
    try {
      await api.head(`call/${callId}/recording/outgoing`);
    } catch (err: any) {
      if (err.response?.status === 404) {
        showToast(
          'Recording file not found. It may have been deleted from the remote server.',
          'error'
        );
        setAudioErrorsShown((prev) => new Set(prev).add(callId));
      }
    }
  };

  const renderFieldValue = (call: Call, key: string): React.ReactNode => {
    switch (key) {
      case 'status':
        return (
          <Chip
            label={capitalize(String(call.status || '-'))}
            size='small'
            color={
              call.status === 'completed'
                ? 'success'
                : call.status === 'failed'
                ? 'error'
                : 'default'
            }
          />
        );
      case 'isActive':
        return call.isActive ? 'Yes' : 'No';
      case 'createdAt':
      case 'updatedAt':
      case 'startedAt':
      case 'answeredAt':
      case 'endedAt':
        return formatDate(
          (call as unknown as Record<string, unknown>)[key] as string
        );
      case 'durationSec':
      case 'maxDurationSec':
      case 'maxAttempts':
      case 'attemptNumber':
        return String((call as unknown as Record<string, unknown>)[key] ?? '-');
      case 'transcript':
        if (!call.transcript || call.transcript.length === 0) return '-';
        return (
          <Stack
            spacing={1}
            sx={{ pl: 2 }}
          >
            {call.transcript.map((t, i) => (
              <Box
                key={i}
                sx={{ pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}
              >
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ fontFamily: 'monospace' }}
                >
                  {t.timestamp ? formatDate(t.timestamp) : '-'}
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ fontFamily: 'monospace' }}
                >
                  <strong>{t.speaker}:</strong> {t.message}
                </Typography>
              </Box>
            ))}
          </Stack>
        );
      default:
        const value = (call as unknown as Record<string, unknown>)[key];
        if (value === undefined || value === null || value === '') {
          return <span style={{ color: '#888' }}>(empty)</span>;
        }
        return String(value);
    }
  };

  const renderCallsContent = () => {
    if (getCallsQuery.isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    const calls = getCallsQuery.data?.[0] ?? [];
    const total = getCallsQuery.data?.[1] ?? 0;

    if (calls.length === 0) {
      return (
        <Typography color='text.secondary'>No calls available.</Typography>
      );
    }

    return (
      <>
        <TableContainer
          component={Paper}
          variant='outlined'
        >
          <Table size='small'>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.100' }}>
                <TableCell></TableCell>
                <TableCell>
                  <Typography variant='subtitle2'>Campaign</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='subtitle2'>Phone Number</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='subtitle2'>Status</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='subtitle2'>Result</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='subtitle2'>Started At</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='subtitle2'>Ended At</Typography>
                </TableCell>
                <TableCell align='right'>
                  <Typography variant='subtitle2'>Actions</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {calls.map((call) => (
                <TableRow
                  key={call.id}
                  hover
                >
                  <TableCell>
                    <audio
                      src={callsAPI.getRecordingUrl(call.id)}
                      controls
                      style={{ height: 30, width: 180 }}
                      preload='none'
                      onError={() => handleAudioError(call.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      {call.campaign?.name || '-'}
                      {call.campaign && (
                        <Tooltip title='View campaign'>
                          <IconButton
                            size='small'
                            onClick={() =>
                              handleOpenViewCampaign(
                                call.campaign as unknown as Record<
                                  string,
                                  unknown
                                >
                              )
                            }
                            color='primary'
                          >
                            <Visibility fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      {formatPhone(call.contact?.phoneNumber)}
                      {call.contact && (
                        <Tooltip title='View contact'>
                          <IconButton
                            size='small'
                            onClick={() =>
                              handleOpenViewContact(
                                call.contact as unknown as Record<
                                  string,
                                  unknown
                                >
                              )
                            }
                            color='primary'
                          >
                            <Visibility fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={capitalize(String(call.status || '-'))}
                      size='small'
                      color={
                        call.status === 'completed'
                          ? 'success'
                          : call.status === 'failed'
                          ? 'error'
                          : 'default'
                      }
                    />
                  </TableCell>
                  <TableCell>{call.result || '-'}</TableCell>
                  <TableCell>{formatDate(call.startedAt)}</TableCell>
                  <TableCell>{formatDate(call.endedAt)}</TableCell>
                  <TableCell align='right'>
                    <Tooltip title='View call'>
                      <IconButton
                        size='small'
                        onClick={() => handleOpenViewCall(call)}
                        color='primary'
                      >
                        <Visibility fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component='div'
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[20]}
          showFirstButton
          showLastButton
        />
      </>
    );
  };

  return (
    <MainLayout>
      <Stack
        spacing={4}
        width='100%'
      >
        <Typography
          variant='h4'
          gutterBottom
        >
          Outgoing Calls
        </Typography>

        <FormControl
          size='small'
          sx={{ minWidth: 240, alignSelf: 'flex-start' }}
        >
          <InputLabel>Campaign Filter</InputLabel>
          <Select
            value={selectedCampaignName}
            label='Campaign Filter'
            onChange={(e) => {
              setSelectedCampaignName(e.target.value as string);
              setPage(0);
            }}
          >
            <MenuItem value=''>
              <em>All Campaigns</em>
            </MenuItem>
            {(getCampaignsForFilterQuery.data || []).map((campaign: any) => (
              <MenuItem
                key={campaign.name}
                value={campaign.name}
              >
                {campaign.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {renderCallsContent()}
      </Stack>

      {/* View Call Modal */}
      <Dialog
        open={viewCallOpen}
        onClose={handleCloseViewCall}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>View Call</DialogTitle>
        <DialogContent>
          {viewCall && (
            <Paper
              variant='outlined'
              sx={{
                p: 2,
                backgroundColor: 'grey.50',
                fontFamily: 'monospace',
              }}
            >
              {FIELD_ORDER_CALL.map((key) => (
                <Box
                  key={key}
                  sx={{ mb: 2 }}
                >
                  <Typography
                    variant='subtitle2'
                    sx={{
                      fontFamily: 'monospace',
                      fontWeight: 600,
                      color: 'primary.main',
                    }}
                  >
                    {FIELD_LABELS_CALL[key]}:
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      pl: 1,
                    }}
                  >
                    {renderFieldValue(viewCall, key)}
                  </Typography>
                </Box>
              ))}
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseViewCall}
            variant='contained'
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Campaign Modal */}
      <Dialog
        open={viewCampaignOpen}
        onClose={handleCloseViewCampaign}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>View Campaign</DialogTitle>
        <DialogContent>
          {viewCampaign && (
            <Paper
              variant='outlined'
              sx={{
                p: 2,
                backgroundColor: 'grey.50',
                fontFamily: 'monospace',
              }}
            >
              {CAMPAIGN_FIELD_ORDER.map((key) => {
                const label = CAMPAIGN_FIELD_LABELS[key] || key;
                let displayValue: React.ReactNode;

                if (key === 'businessDays') {
                  let days = viewCampaign[key] as
                    | Record<string, boolean>
                    | string
                    | undefined;
                  if (!days) {
                    displayValue = '-';
                  } else {
                    if (typeof days === 'string') {
                      try {
                        days = JSON.parse(days) as Record<string, boolean>;
                      } catch {
                        displayValue = String(days);
                      }
                    }
                    if (typeof days === 'object' && days !== null) {
                      const active = Object.entries(days)
                        .filter(([, v]) => v)
                        .map(([k]) => capitalize(k))
                        .join(', ');
                      displayValue = active || '-';
                    }
                  }
                } else if (key === 'businessHours') {
                  const start = viewCampaign.startTime as string | undefined;
                  const end = viewCampaign.endTime as string | undefined;
                  displayValue = start && end ? `${start} - ${end}` : '-';
                } else {
                  const val = viewCampaign[key];
                  if (val === undefined || val === null || val === '') {
                    displayValue = '-';
                  } else if (isDateString(val)) {
                    displayValue = formatDate(val);
                  } else if (typeof val === 'object') {
                    displayValue = JSON.stringify(val, null, 2);
                  } else {
                    displayValue = String(val);
                  }
                }

                return (
                  <Box
                    key={key}
                    sx={{ mb: 2 }}
                  >
                    <Typography
                      variant='subtitle2'
                      sx={{
                        fontFamily: 'monospace',
                        fontWeight: 600,
                        color: 'primary.main',
                      }}
                    >
                      {label}:
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        pl: 1,
                      }}
                    >
                      {displayValue}
                    </Typography>
                  </Box>
                );
              })}
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseViewCampaign}
            variant='contained'
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Contact Modal */}
      <Dialog
        open={viewContactOpen}
        onClose={handleCloseViewContact}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>View Contact</DialogTitle>
        <DialogContent>
          {viewContact && (
            <Paper
              variant='outlined'
              sx={{
                p: 2,
                backgroundColor: 'grey.50',
                fontFamily: 'monospace',
              }}
            >
              {Object.entries(viewContact)
                .filter(
                  ([key]) =>
                    ![
                      'id',
                      'isActive',
                      'lastCallId',
                      'notes',
                      'updatedAt',
                    ].includes(key)
                )
                .map(([key, value]) => {
                  const label = camelToTitle(key);
                  let displayValue: React.ReactNode;
                  if (value === undefined || value === null || value === '') {
                    displayValue = '-';
                  } else if (isDateString(value)) {
                    displayValue = formatDate(value);
                  } else if (typeof value === 'object') {
                    displayValue = JSON.stringify(value, null, 2);
                  } else {
                    displayValue = String(value);
                  }

                  return (
                    <Box
                      key={key}
                      sx={{ mb: 2 }}
                    >
                      <Typography
                        variant='subtitle2'
                        sx={{
                          fontFamily: 'monospace',
                          fontWeight: 600,
                          color: 'primary.main',
                        }}
                      >
                        {label}:
                      </Typography>
                      <Typography
                        variant='body2'
                        sx={{
                          fontFamily: 'monospace',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          pl: 1,
                        }}
                      >
                        {displayValue}
                      </Typography>
                    </Box>
                  );
                })}
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseViewContact}
            variant='contained'
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
};
